import { useEffect, useRef, useState, useCallback } from 'react';
import NetWorthCard from '@/components/dashboard/NetWorthCard';
import IncomeCard from '@/components/dashboard/IncomeCard';
import ExpensesCard from '@/components/dashboard/ExpensesCard';
import SavingsRateCard from '@/components/dashboard/SavingsRateCard';
import RecentTransactionsCard from '@/components/dashboard/RecentTransactionsCard';
import BudgetOverviewCard from '@/components/dashboard/BudgetOverviewCard';
import { TransactionInsightsCard } from '@/components/transactions/TransactionInsightsCard';
import FinancialGoalsCard from '@/components/dashboard/FinancialGoalsCard';
import UpcomingBillsCard from '@/components/dashboard/UpcomingBillsCard';
import PageGuide from '@/components/common/PageGuide';
import { dashboardGuide } from '@/components/common/guideContent';
import { CashFlowChart } from '@/components/dashboard/CashFlowChart';
import { SpendingByCategoryChart } from '@/components/dashboard/SpendingByCategoryChart';
import TransactionForm from '@/components/transactions/TransactionForm';
import type { TransactionFormData } from '@/components/transactions/TransactionForm';
import DataHealthPanel from '@/components/health/DataHealthPanel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { fetchAccounts } from '@/features/accounts/accountsSlice';
import {
  fetchTransactions,
  updateTransaction,
  deleteTransaction,
} from '@/features/transactions/transactionsSlice';
import { fetchBudgets } from '@/features/budgets/budgetsSlice';
import { fetchGoals } from '@/features/goals/goalsSlice';
import { fetchCategories } from '@/features/categories/categoriesSlice';
import { useToast } from '@/hooks/use-toast';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { Transaction } from '@/lib/types';
// [T27P1] Bills feature has no backend endpoint yet — keep mock data for UpcomingBillsCard
import { mockBills } from '@/lib/data';

export default function LegacyDashboard() {
  useDocumentTitle('Dashboard');
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { accounts, isLoading: accountsLoading } = useAppSelector((state) => state.accounts);
  const { transactions, isLoading: transactionsLoading } = useAppSelector((state) => state.transactions);
  const { budgets, isLoading: budgetsLoading } = useAppSelector((state) => state.budgets);
  const { goals, isLoading: goalsLoading } = useAppSelector((state) => state.goals);
  const { categories } = useAppSelector((state) => state.categories);

  // [T38P0] Dashboard-level edit/delete state for RecentTransactionsCard
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // [T28P0] Guard against React StrictMode double-invocation of effects.
  // StrictMode in development mounts → unmounts → remounts, causing effects to fire
  // twice. This ref prevents duplicate API calls on the second mount.
  const hasFetched = useRef(false);

  // [T27P1] Fetch real data from API instead of using hardcoded mock data.
  // Previous implementation used mockAccounts/mockTransactions/etc from data.ts,
  // causing the Dashboard to show fake transactions while the Transactions page
  // showed "No transactions yet" from the real API.
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    dispatch(fetchAccounts());
    dispatch(fetchTransactions({ limit: 20, sortBy: 'date', sortOrder: 'desc' }));
    dispatch(fetchBudgets());
    dispatch(fetchGoals());
    dispatch(fetchCategories());
  }, [dispatch]);

  // [T38P0] Reload latest transactions after an edit/delete from the dashboard
  const reloadTransactions = useCallback(() => {
    dispatch(fetchTransactions({ limit: 20, sortBy: 'date', sortOrder: 'desc' }));
  }, [dispatch]);

  // [T38P0] Handle transaction update triggered from RecentTransactionsCard
  const handleUpdate = async (data: TransactionFormData) => {
    if (!editingTransaction) return;
    setFormLoading(true);
    try {
      await dispatch(
        updateTransaction({
          id: editingTransaction.id,
          data: {
            type: data.type,
            amount: data.amount,
            description: data.description,
            date: data.date.toISOString(),
            accountId: data.accountId,
            categoryId:
              data.categoryId && data.categoryId !== 'none'
                ? data.categoryId
                : undefined,
            currency: data.currency || undefined,
            notes: data.notes || undefined,
            tags: data.tags?.map((tag) => tag.trim()).filter(Boolean) ?? [],
          },
        })
      ).unwrap();
      toast({ title: 'Transaction updated' });
      setEditingTransaction(null);
      reloadTransactions();
    } catch (err: unknown) {
      toast({
        title: 'Error',
        description: String(err),
        variant: 'destructive',
      });
    } finally {
      setFormLoading(false);
    }
  };

  // [T38P0] Handle transaction delete triggered from RecentTransactionsCard
  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await dispatch(deleteTransaction(deletingId)).unwrap();
      toast({ title: 'Transaction deleted' });
      setDeletingId(null);
      reloadTransactions();
    } catch (err: unknown) {
      toast({
        title: 'Error',
        description: String(err),
        variant: 'destructive',
      });
    }
  };

  const isLoading = accountsLoading || transactionsLoading || budgetsLoading || goalsLoading;
  if (isLoading && accounts.length === 0 && transactions.length === 0) {
    // Keep PageGuide mounted while data loads so onboarding affordance is
    // visible immediately (important under concurrent-worker load where API
    // responses can exceed 10s).
    return (
      <div className="space-y-4 md:space-y-8">
        <PageGuide pageKey="dashboard" content={dashboardGuide} />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
            <p className="mt-4 text-sm text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-8">
      <PageGuide pageKey="dashboard" content={dashboardGuide} />
      <div className="flex flex-col gap-1 md:gap-2">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Dashboard</h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Welcome back! Here's an overview of your finances.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <NetWorthCard accounts={accounts} transactions={transactions} />
        <IncomeCard transactions={transactions} />
        <ExpensesCard transactions={transactions} />
        <SavingsRateCard transactions={transactions} />
      </div>

      {/* Main Content */}
      <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
        <div className="min-w-0 overflow-hidden">
          <RecentTransactionsCard
            transactions={transactions}
            accounts={accounts}
            categories={categories}
            isLoading={transactionsLoading}
            onEdit={(txn) => setEditingTransaction(txn)}
            onDelete={(id) => setDeletingId(id)}
          />
        </div>

        <div className="min-w-0 overflow-hidden">
          {/* [T61P0] Transaction Insights replaces Budget Overview in Main Content */}
          <TransactionInsightsCard
            variant="dashboard"
            transactions={transactions}
            accounts={accounts}
            categories={categories}
          />
        </div>
      </div>

      {/* Insights */}
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-semibold">Insights</h3>
          <p className="text-sm text-muted-foreground">
            Visualize cash flow and spending patterns.
          </p>
        </div>
        {/* [T46P0] min-w-0 on grid children prevents chart SVG from blowing out grid cells on mobile */}
        <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
          <div className="min-w-0 overflow-hidden">
            <CashFlowChart transactions={transactions} />
          </div>
          <div className="min-w-0 overflow-hidden">
            <SpendingByCategoryChart transactions={transactions} />
          </div>
        </div>
        {/* [T61P0] Budget Overview moved here from Main Content */}
        <div className="min-w-0 overflow-hidden">
          <BudgetOverviewCard budgets={budgets} />
        </div>
      </div>

      <div className="grid gap-4 md:gap-6 md:grid-cols-2">
        <FinancialGoalsCard goals={goals} />
        <UpcomingBillsCard bills={mockBills} />
      </div>

      {/* [T155P3] Data Health Hub — summary widget */}
      <div data-testid="dashboard-health-widget">
        <div className="md:hidden">
          <DataHealthPanel compact />
        </div>
        <div className="hidden md:block">
          <DataHealthPanel />
        </div>
      </div>

      {/* [T38P0] Edit transaction form — opened via RecentTransactionsCard */}
      <TransactionForm
        open={!!editingTransaction}
        onOpenChange={(open) => !open && setEditingTransaction(null)}
        onSubmit={handleUpdate}
        transaction={editingTransaction}
        accounts={accounts}
        categories={categories}
        isLoading={formLoading}
      />

      {/* [T38P0] Delete confirmation — opened via RecentTransactionsCard */}
      <AlertDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This action cannot be undone — the account balance
              will be adjusted accordingly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
